import type { DocumentMentionElement } from 'src/components/ai/chat/ChatPanel/ChatEditor/ChatEditorMention/utils';
import type { ReportGridRow } from 'src/components/reports/types';

/**
 * Minimal shape of a selected repository row needed to build a chat mention.
 * A full `ReportGridRow` satisfies this, but the helpers below only read the
 * document id and name so tests can pass plain objects.
 */
export type SelectableDocumentRow = Pick<ReportGridRow, 'documentId' | 'name'>;

/**
 * Whether the "Add to Chat" toggle should read "Remove from Chat".
 * True only when there is a selection AND every selected document is already a
 * chip in the chat input — a single not-yet-added document keeps the toggle on
 * "Add to Chat" so the click adds the remainder rather than removing everything.
 */
export function getAllSelectedDocumentsInChat(
  selectedRows: readonly SelectableDocumentRow[],
  chatDocumentIdSet: ReadonlySet<string>
): boolean {
  return (
    selectedRows.length > 0 && selectedRows.every(row => chatDocumentIdSet.has(row.documentId))
  );
}

/**
 * Whether the current selection stands for documents that aren't loaded in the
 * grid, and so can't be faithfully turned into chat mentions (which need each
 * document's id AND name). A fully-selected group and a repository-wide
 * select-all are resolved server-side by other bulk actions via
 * `groupSelection` / `runAllDocs`, so their totals can exceed the loaded rows.
 * A small, fully-loaded select-all has `representedCount === loadedCount` and
 * stays supported — only genuinely unloaded documents trip the guard.
 */
export function getHasUnloadedSelectedDocuments({
  loadedSelectedDocumentCount,
  selectedGroupDocCount,
  shouldRunForAllDocuments,
  totalDocumentCount,
}: {
  loadedSelectedDocumentCount: number;
  selectedGroupDocCount: number | null;
  shouldRunForAllDocuments: boolean;
  totalDocumentCount: number | 'no-select-all';
}): boolean {
  const representedDocumentCount =
    selectedGroupDocCount !== null
      ? selectedGroupDocCount
      : shouldRunForAllDocuments && typeof totalDocumentCount === 'number'
        ? totalDocumentCount
        : loadedSelectedDocumentCount;
  return representedDocumentCount > loadedSelectedDocumentCount;
}

/**
 * Resolves the document mentions to insert for an "Add to Chat" click.
 *
 * Selected rows are first deduped by document id (the grid can surface the same
 * document under multiple tree rows). Any mentions still queued from an earlier
 * not-ready insert are folded in ahead of the fresh selection so they aren't
 * stranded. The combined list is then filtered against the chat's current chip
 * set — `insertDocumentMentions` does not dedupe, so passing an already-present
 * document id would create a duplicate chip.
 */
export function buildDocumentMentionsToInsert({
  chatDocumentIdSet,
  queuedMentions,
  selectedRows,
}: {
  chatDocumentIdSet: ReadonlySet<string>;
  queuedMentions: readonly DocumentMentionElement[];
  selectedRows: readonly SelectableDocumentRow[];
}): DocumentMentionElement[] {
  const seenDocumentIds = new Set<string>();
  const selectedMentions = selectedRows.flatMap(row => {
    if (seenDocumentIds.has(row.documentId)) return [];
    seenDocumentIds.add(row.documentId);
    return [toDocumentMention(row)];
  });

  const insertSeenIds = new Set(chatDocumentIdSet);
  return [...queuedMentions, ...selectedMentions].filter(mention => {
    if (insertSeenIds.has(mention.documentId)) return false;
    insertSeenIds.add(mention.documentId);
    return true;
  });
}

function toDocumentMention(row: SelectableDocumentRow): DocumentMentionElement {
  return {
    children: [{ text: '' }],
    documentId: row.documentId,
    documentName: row.name,
    mentionTypeName: 'document',
    type: 'mention',
    value: row.name,
  };
}
